import React, { useState, useMemo } from 'react';
import { ClipLoader } from 'react-spinners';
import { FaDownload, FaFilter } from 'react-icons/fa';
import '../Reports.css';

function AgendamentosTab({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    bookingsLoading,
    bookings,
    handleSearch,
    handleQuickFilter,
    setBookings,
    professionals,
    selectedProfessional,
    setSelectedProfessional
}) {
    const [selectedProcedure, setSelectedProcedure] = useState('all');

    // Flatten bookings into individual procedure rows
    const allRows = useMemo(() => {
        const getProfessionalNameInternal = (professionalId) => {
            const professional = professionals.find(p => p.id === professionalId);
            return professional ? professional.name : professionalId;
        };

        const rows = [];
        const realizadoBookings = bookings.filter(booking => booking.status === 'Realizado');

        realizadoBookings.forEach(booking => {
            const procedures = booking.procedimentosList || booking.procedimentos || [];
            
            if (procedures.length === 0) {
                rows.push({
                    id: `${booking.booking_id || booking.id}-empty`,
                    data: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[0] : '',
                    hora: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[1]?.replace('Z', '').substring(0, 5) : '',
                    paciente: booking.paciente || '',
                    profissional: getProfessionalNameInternal(booking.profissional),
                    procedimento: '',
                    duracao: booking.duration || '',
                    status: booking.status || '',
                    observacao: booking.observacao || '',
                    canal: booking.booking_channel || '',
                    agente: booking.booking_agent || ''
                });
            } else {
                procedures.forEach((proc, idx) => {
                    rows.push({
                        id: `${booking.booking_id || booking.id}-${idx}`,
                        data: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[0] : '',
                        hora: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[1]?.replace('Z', '').substring(0, 5) : '',
                        paciente: booking.paciente || '',
                        profissional: getProfessionalNameInternal(booking.profissional),
                        procedimento: proc.nome || '',
                        duracao: booking.duration || '',
                        status: booking.status || '',
                        observacao: booking.observacao || '',
                        canal: booking.booking_channel || '',
                        agente: booking.booking_agent || ''
                    });
                });
            }
        });
        return rows;
    }, [bookings, professionals]);

    // Get unique procedure names for the dropdown
    const uniqueProcedures = useMemo(() => {
        const procedures = new Set();
        allRows.forEach(row => {
            if (row.procedimento) procedures.add(row.procedimento);
        });
        return Array.from(procedures).sort();
    }, [allRows]);

    // Filter rows based on selected procedure
    const filteredRows = useMemo(() => {
        if (selectedProcedure === 'all') return allRows;
        return allRows.filter(row => row.procedimento === selectedProcedure);
    }, [allRows, selectedProcedure]);

    const downloadCSV = () => {
        if (filteredRows.length === 0) {
            alert('Nenhum dado para exportar');
            return;
        }

        const headers = ['Data', 'Hora', 'Paciente', 'Profissional', 'Procedimento', 'Duracao (min)', 'Status', 'Observacao', 'Canal', 'Agente'];
        
        const escapeCSV = (value) => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.join(','),
            ...filteredRows.map(row => [
                escapeCSV(row.data),
                escapeCSV(row.hora),
                escapeCSV(row.paciente),
                escapeCSV(row.profissional),
                escapeCSV(row.procedimento),
                escapeCSV(row.duracao),
                escapeCSV(row.status),
                escapeCSV(row.observacao),
                escapeCSV(row.canal),
                escapeCSV(row.agente)
            ].join(','))
        ].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `relatorio_agendamentos_${startDate}_${endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="caixa-container">
            <div className="caixa-search-filters">
                <div className="date-filters">
                    <div className="quick-filters">
                        <button className="quick-filter-button" onClick={() => handleQuickFilter(7)}>7d</button>
                        <button className="quick-filter-button" onClick={() => handleQuickFilter(15)}>15d</button>
                        <button className="quick-filter-button" onClick={() => handleQuickFilter(30)}>30d</button>
                    </div>
                    <div className="date-input-group">
                        <label>Início:</label>
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setBookings([]); 
                            }}
                            className="date-input"
                            required
                        />
                    </div>
                    <div className="date-input-group">
                        <label>Fim:</label>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setBookings([]);
                            }}
                            className="date-input"
                            required
                        />
                    </div>
                    <button className="dark-button" onClick={handleSearch} disabled={bookingsLoading}>Filtrar</button>

                    <div className="date-input-group" style={{ marginLeft: '10px' }}>
                        <label><FaFilter style={{ marginRight: '5px' }} /> Profissional:</label>
                        <select
                            className="status-select"
                            value={selectedProfessional}
                            onChange={(e) => {
                                setSelectedProfessional(e.target.value);
                                setBookings([]);
                            }}
                        >
                            <option value="all">Todos os Profissionais</option>
                            {professionals.map(professional => (
                                <option key={professional.id} value={professional.id}>
                                    {professional.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="date-input-group" style={{ marginLeft: '10px' }}>
                        <label><FaFilter style={{ marginRight: '5px' }} /> Procedimento:</label>
                        <select 
                            className="status-select"
                            value={selectedProcedure}
                            onChange={(e) => {
                                setSelectedProcedure(e.target.value);
                                handleSearch();
                            }}
                        >
                            <option value="all">Todos os Procedimentos</option>
                            {uniqueProcedures.map(proc => (
                                <option key={proc} value={proc}>{proc}</option>
                            ))}
                        </select>
                    </div>

                    <button 
                        className="dark-button"
                        onClick={downloadCSV}
                        disabled={bookingsLoading || filteredRows.length === 0}
                        title="Baixar CSV deste relatório"
                    >
                        <FaDownload style={{ marginRight: '5px' }} />
                        Baixar CSV
                    </button>
                </div>
            </div>

            {bookingsLoading ? (
                <div className="loading-container">
                    <ClipLoader size={50} color={"#4a90e2"} />
                </div>
            ) : (
                <div className="bookings-list">
                    <div className="procedure-group">
                        <h3>Relatório de Agendamentos Detalhado</h3>
                        <div style={{ overflowX: 'auto' }}>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Hora</th>
                                        <th>Paciente</th>
                                        <th>Profissional</th>
                                        <th>Procedimento</th>
                                        <th>Duração</th>
                                        <th>Status</th>
                                        <th>Observação</th>
                                        <th>Canal</th>
                                        <th>Agente</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.data}</td>
                                            <td>{row.hora}</td>
                                            <td>{row.paciente}</td>
                                            <td>{row.profissional}</td>
                                            <td><strong>{row.procedimento}</strong></td>
                                            <td>{row.duracao} min</td>
                                            <td>{row.status}</td>
                                            <td title={row.observacao}>{row.observacao && row.observacao.length > 20 ? row.observacao.substring(0, 20) + '...' : row.observacao}</td>
                                            <td>{row.canal}</td>
                                            <td>{row.agente}</td>
                                        </tr>
                                    ))}
                                    {filteredRows.length === 0 && !bookingsLoading && (
                                        <tr>
                                            <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                                Nenhum agendamento encontrado no período selecionado
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AgendamentosTab;
